import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, AlertCircle, Search, Filter } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContadoVencidosReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [providerSearch, setProviderSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payments/contado-overdue`);
      // Defensa adicional en frontend: solo contado real
      const todayStr = new Date().toISOString().slice(0, 10);
      const safe = (response.data || []).filter(p => {
        if ((p.tipo_pago || '').toLowerCase() !== 'contado') return false;
        if (p.fecha_pago_programada) {
          try {
            const fpp = String(p.fecha_pago_programada).slice(0, 10);
            if (fpp > todayStr) return false;
          } catch {
            /* ignore */
          }
        }
        return true;
      });
      setItems(safe);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para ver este reporte');
        navigate('/dashboard');
      } else {
        toast.error('Error al cargar el reporte');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = providerSearch.trim().toLowerCase();
    return items.filter(p => {
      if (term) {
        const name = (p.proveedor_nombre || p.proveedor || '').toLowerCase();
        if (!name.includes(term)) return false;
      }
      if (startDate || endDate) {
        const createdStr = String(p.created_at).slice(0, 10);
        if (startDate && createdStr < startDate) return false;
        if (endDate && createdStr > endDate) return false;
      }
      return true;
    });
  }, [items, providerSearch, startDate, endDate]);

  const totals = useMemo(() => {
    const total = filtered.reduce((s, p) => s + (p.monto_depositar || 0), 0);
    const providers = new Set(filtered.map(p => p.proveedor_nombre || p.proveedor)).size;
    return { total, providers, count: filtered.length };
  }, [filtered]);

  const daysOverdue = (createdAt) => {
    try {
      const d = new Date(createdAt);
      const today = new Date();
      const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(diff, 0);
    } catch {
      return 0;
    }
  };

  const clearFilters = () => {
    setProviderSearch('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contado-vencidos-report-page">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-red-500 flex items-center justify-center shadow-sm">
            <AlertCircle className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Contado Vencidos</h1>
            <p className="text-sm text-slate-500">Facturas al contado pendientes de pagar creadas antes de hoy</p>
          </div>
        </div>

        {/* Filtros */}
        <Card className="p-4 mb-4 bg-white border-slate-200" data-testid="filters-card">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center text-sm">
            <Filter className="mr-2 h-4 w-4" />
            Filtros
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Proveedor</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar proveedor..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  className="pl-8"
                  data-testid="provider-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
                data-testid="end-date-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full"
                data-testid="clear-filters-button"
              >
                Limpiar
              </Button>
            </div>
          </div>
        </Card>

        {/* Totales */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Card className="p-3 bg-red-50 border-red-200">
            <p className="text-xs text-red-600">Facturas Pendientes</p>
            <p className="text-2xl font-bold text-red-700" data-testid="total-count">{totals.count}</p>
          </Card>
          <Card className="p-3 bg-red-50 border-red-200">
            <p className="text-xs text-red-600">Monto Total</p>
            <p className="text-2xl font-bold text-red-700" data-testid="total-amount">
              Q{totals.total.toLocaleString('es-GT', {minimumFractionDigits: 2})}
            </p>
          </Card>
          <Card className="p-3 bg-red-50 border-red-200">
            <p className="text-xs text-red-600">Proveedores</p>
            <p className="text-2xl font-bold text-red-700" data-testid="total-providers">{totals.providers}</p>
          </Card>
        </div>

        {/* Lista */}
        {loading ? (
          <Card className="p-8 text-center">
            <p className="text-slate-600">Cargando reporte...</p>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="p-8 text-center" data-testid="empty-state">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No hay facturas contado vencidas con los filtros aplicados</p>
          </Card>
        ) : (
          <div className="grid gap-2">
            {filtered.map(p => {
              const fechaTxt = (() => {
                try {
                  return format(new Date(p.created_at), "d 'de' MMM yyyy", { locale: es });
                } catch {
                  return '';
                }
              })();
              const dias = daysOverdue(p.created_at);
              return (
                <Card
                  key={p.id}
                  className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/payments/${p.id}`)}
                  data-testid={`payment-row-${p.id}`}
                >
                  <div className="p-3 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {p.proveedor_nombre || p.proveedor}
                        </span>
                        <Badge className="bg-red-600 text-white text-[10px] px-2 py-0">VENCIDO {dias}d</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {p.tienda_nombre || p.centro_costo || 'Sin tienda'} &middot; Creado el {fechaTxt}
                      </p>
                      {p.usuario_nombre && (
                        <p className="text-xs text-slate-400 mt-0.5">Reportado por: {p.usuario_nombre}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold text-red-700">
                        Q{(p.monto_depositar || 0).toLocaleString('es-GT', {minimumFractionDigits: 2})}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
